import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import FileInput, {File} from "../../../../shared/components/FileInput";
import {Dropdown} from "primereact/dropdown";
import {WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputNumber} from "primereact/inputnumber";
import {Slider} from "primereact/slider";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import PdfaDialogFields from "../../field/convert/PdfaDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {InputSwitch} from "primereact/inputswitch";
import {
	AdminWebserviceStatus,
	AnalyzePdfa,
	ConvertPdfa,
	Functions,
	Information,
	Pdfa,
	PdfaErrorCorrection,
	PdfaLevel,
	Settings,
	SignatureEditProfile,
	ZugferdFileData,
	ZugferdVersion
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PdfaMode, PortalPdfa} from "../../../../shared/models/PortalPdfa";
import {PortalZugferdFileData} from "../../../../shared/models/PortalZugferdFileData";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import ConcurrentOperation from "../../../../shared/class/ConcurrentOperation";
import Exception from "../../../../shared/exception/Exception";
import CodeInput from "../../../../shared/components/CodeInput";
import {Base64} from "js-base64";

interface PdfaDialogStatus extends PortalDialogState<PortalPdfa> {
	zugferdFiles: Map<ZugferdVersion, File>;
	isArchive: boolean
	filePreview: string;
}

export class PdfaDialogDefinition extends PortalDialog<PortalPdfa, PortalDialogProperties<PortalPdfa>,
	PdfaDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			zugferdFiles: new Map<ZugferdVersion, File>(),
			isArchive: false,
			fieldErrors: new Map<PdfaDialogFields, string>(),
			filePreview: ""
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.pdfa?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.pdfaConversion, Functions.getPdfaConversionDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.PDFA, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkArchive: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length >= 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: PortalPdfa | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid" hidden={
						parameter?.mode !== PdfaMode.CONVERT ||
						!this.state.fields?.contains(PdfaDialogFields.FORMAT)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-convert-format">
							<Translate {...this.props} data="pdfa.format.label" namespace="dialog"/>
							<Info {...this.props} data="pdfa.format.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-convert-format"
									  value={FunctionHelper.applyValue(
										  parameter?.convert?.level, ConvertPdfa.getLevelDefault()
									  )}
									  options={[
										  {
											  label: Translate.value("pdfa.format.1a", {ns: "dialog"}),
											  value: PdfaLevel._1a
										  },
										  {
											  label: Translate.value("pdfa.format.1b", {ns: "dialog"}),
											  value: PdfaLevel._1b
										  },
										  {
											  label: Translate.value("pdfa.format.2a", {ns: "dialog"}),
											  value: PdfaLevel._2a
										  },
										  {
											  label: Translate.value("pdfa.format.2b", {ns: "dialog"}),
											  value: PdfaLevel._2b
										  },
										  {
											  label: Translate.value("pdfa.format.2u", {ns: "dialog"}),
											  value: PdfaLevel._2u
										  },
										  {
											  label: Translate.value("pdfa.format.3a", {ns: "dialog"}),
											  value: PdfaLevel._3a
										  },
										  {
											  label: Translate.value("pdfa.format.3b", {ns: "dialog"}),
											  value: PdfaLevel._3b
										  },
										  {
											  label: Translate.value("pdfa.format.3u", {ns: "dialog"}),
											  value: PdfaLevel._3u
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  PdfaDialogFields.FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						parameter?.mode !== PdfaMode.ANALYZE ||
						!this.state.fields?.contains(PdfaDialogFields.FORMAT)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-analyse-format">
							<Translate {...this.props} data="pdfa.format.label" namespace="dialog"/>
							<Info {...this.props} data="pdfa.format.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-analyse-format"
									  value={FunctionHelper.applyValue(
										  parameter?.analyze?.level, AnalyzePdfa.getLevelDefault()
									  )}
									  options={[
										  {
											  label: Translate.value("pdfa.format.1a", {ns: "dialog"}),
											  value: PdfaLevel._1a
										  },
										  {
											  label: Translate.value("pdfa.format.1b", {ns: "dialog"}),
											  value: PdfaLevel._1b
										  },
										  {
											  label: Translate.value("pdfa.format.2a", {ns: "dialog"}),
											  value: PdfaLevel._2a
										  },
										  {
											  label: Translate.value("pdfa.format.2b", {ns: "dialog"}),
											  value: PdfaLevel._2b
										  },
										  {
											  label: Translate.value("pdfa.format.2u", {ns: "dialog"}),
											  value: PdfaLevel._2u
										  },
										  {
											  label: Translate.value("pdfa.format.3a", {ns: "dialog"}),
											  value: PdfaLevel._3a
										  },
										  {
											  label: Translate.value("pdfa.format.3b", {ns: "dialog"}),
											  value: PdfaLevel._3b
										  },
										  {
											  label: Translate.value("pdfa.format.3u", {ns: "dialog"}),
											  value: PdfaLevel._3u
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  PdfaDialogFields.FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.pdfa.format"} namespace={"help"} markdown={true}/>
					</Helper>
					<div className="p-col-12 p-grid" hidden={
						parameter?.mode !== PdfaMode.CONVERT ||
						!this.state.fields?.contains(PdfaDialogFields.RESOLUTION)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-resolution">
							<Translate {...this.props} data="pdfa.image.resolution" namespace="dialog"/>
							<Info {...this.props} data="pdfa.image.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 slider-input">
							<InputNumber inputId="control-resolution" step={1} suffix=" %" showButtons={true}
										 value={FunctionHelper.applyValue(
											 parameter?.convert?.imageQuality, ConvertPdfa.getImageQualityDefault()
										 )}
										 min={ConvertPdfa.getImageQualityMin()} useGrouping={false}
										 max={ConvertPdfa.getImageQualityMax()}
										 onValueChange={(e) => this.onChange(
											 PdfaDialogFields.RESOLUTION, e.value
										 )}/>
							<Slider min={ConvertPdfa.getImageQualityMin()} step={1}
									max={ConvertPdfa.getImageQualityMax()}
									value={FunctionHelper.applyValue(
										parameter?.convert?.imageQuality, ConvertPdfa.getImageQualityDefault()
									)}
									onChange={(e) => this.onChange(
										PdfaDialogFields.RESOLUTION, e.value as number
									)}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						parameter?.mode !== PdfaMode.CONVERT ||
						!this.state.fields?.contains(PdfaDialogFields.ERROR_CORRECTION)
					}>
						<label className="p-col-12 p-sm-4"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-error-correction"
										 checked={FunctionHelper.applyValue(
											 parameter?.convert?.errorCorrection?.enabled,
											 PdfaErrorCorrection.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 PdfaDialogFields.ERROR_CORRECTION, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-error-correction">
								<Translate {...this.props} data="pdfa.error_correction.label" namespace="dialog"/>
								<Info {...this.props} data="pdfa.error_correction.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					parameter?.mode !== PdfaMode.CONVERT ||
					(
						parameter?.convert?.level !== PdfaLevel._3a &&
						parameter?.convert?.level !== PdfaLevel._3b &&
						parameter?.convert?.level !== PdfaLevel._3u
					)
				}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(PdfaDialogFields.ZUGFERD)
					}>
						<label className="p-col-12 p-sm-4"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-zugferd"
										 checked={FunctionHelper.applyValue(
											 parameter?.embedZugferd, PortalPdfa.getEmbedZugferdDefault()
										 )}
										 onChange={(e) => this.onChange(
											 PdfaDialogFields.ZUGFERD, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-zugferd">
								<Translate {...this.props} data="pdfa.zugferd.embed" namespace="dialog"/>
								<Info {...this.props} data="pdfa.zugferd.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!parameter?.embedZugferd || !this.state.fields?.contains(PdfaDialogFields.VERSION)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-version">
							<Translate {...this.props} data="pdfa.zugferd.version.label" namespace="dialog"/>
							<Info {...this.props} data="pdfa.zugferd.version.description" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-version"
									  value={FunctionHelper.applyValue(
										  parameter?.convert?.zugferd?.xmlFile?.version,
										  ZugferdFileData.getVersionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.10_comfort", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V10Comfort
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.20_minimum", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V20Minimum
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.20_basic_wl", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V20BasicWL
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.20_basic", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V20Basic
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.20_en16931", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V20EN16931
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.20_extended", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V20Extended
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.21_minimum", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V21Minimum
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.21_basic_wl", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V21BasicWL
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.21_basic", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V21Basic
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.21_en16931", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V21EN16931
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.21_extended", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V21Extended
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.21_xrechnung", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V21XRechnung
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.22_minimum", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V22Minimum
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.22_basic_wl", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V22BasicWL
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.22_basic", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V22Basic
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.22_en16931", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V22EN16931
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.22_extended", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V22Extended
										  },
										  {
											  label: Translate.value(
												  "pdfa.zugferd.version.22_xrechnung", {ns: "dialog"}
											  ),
											  value: ZugferdVersion.V22XRechnung
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  PdfaDialogFields.VERSION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!parameter?.embedZugferd || !this.state.fields?.contains(PdfaDialogFields.FILE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-file">
							<Translate {...this.props} data="pdfa.zugferd.file" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<FileInput inputId="control-file" filename={
								FunctionHelper.applyValue(
									this.state.zugferdFiles.get(FunctionHelper.applyValue(
										parameter?.convert?.zugferd?.xmlFile?.version,
										ZugferdFileData.getVersionDefault()
									))?.name, ""
								)
							} accept="text/xml"
									   value={FunctionHelper.applyValue(
										   parameter?.convert?.zugferd?.xmlFile?.value, ""
									   )}
									   onChange={(files) => this.onChange(PdfaDialogFields.FILE, files[0])}/>
						</div>
						<div className="p-col-12">
							{!FunctionHelper.isEmpty(this.state.filePreview) &&
                                <CodeInput inputId="control-file-content" language={"xml"} height={"300px"}
                                           value={this.state.filePreview} hideButtons={true}
                                           options={{
											   readOnly: true,
											   contextmenu: false,
											   minimap: {
												   enabled: false
											   }
										   }}/>
							}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["pdfa-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.pdfa.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.pdfa.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: PdfaDialogFields, value: any): void {
		let parameter: PortalPdfa = this.state.parameter!.data;

		switch (field) {
			case PdfaDialogFields.FORMAT:
				if (parameter.mode === PdfaMode.CONVERT) {
					parameter.convert!.level = value;
				} else {
					parameter.analyze!.level = value;
				}
				break;
			case PdfaDialogFields.FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let currentVersion = FunctionHelper.applyValue(
					parameter.convert?.zugferd?.xmlFile?.version, ZugferdFileData.getVersionDefault()
				);

				let file: File = value;
				this.state.zugferdFiles.set(currentVersion, file);

				let fileDataType: PortalZugferdFileData = PortalZugferdFileData.fromJson({
					name: file.name,
					mimetype: file.mimetype,
					value: file.value,
					version: currentVersion
				} as PortalZugferdFileData);
				parameter.convert!.zugferd!.xmlFile = fileDataType;

				this.setState({
					zugferdFiles: this.state.zugferdFiles,
					parameter: this.state.parameter,
					filePreview: Base64.atob(file.value)
				});
				return;
			case PdfaDialogFields.RESOLUTION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ConvertPdfa.getImageQualityDefault();
				}

				parameter.convert!.imageQuality = value;
				break;
			case PdfaDialogFields.VERSION:
				let versionFile: File | undefined = this.state.zugferdFiles.get(value);

				parameter.convert!.zugferd!.xmlFile = PortalZugferdFileData.fromJson({
					name: FunctionHelper.applyValue(versionFile?.name, ""),
					mimetype: FunctionHelper.applyValue(versionFile?.mimetype, ""),
					value: FunctionHelper.applyValue(versionFile?.value, ""),
					version: value
				} as PortalZugferdFileData);

				this.setState({
					parameter: this.state.parameter,
					filePreview: Base64.atob(FunctionHelper.applyValue(versionFile?.value, ""))
				});
				return;
			case PdfaDialogFields.ERROR_CORRECTION:
				parameter.convert!.errorCorrection!.enabled = value;
				break;
			case PdfaDialogFields.ZUGFERD:
				parameter.embedZugferd = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1><Icon icon={"icon-archive small"}/>
					{this.state.isArchive ?
						<Translate {...this.props} data="pdfa.check" namespace="dialog"/> :
						<Translate {...this.props} data="pdfa.title" namespace="dialog"/>
					}
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalPdfa | undefined = this.state.parameter?.data;
		let portalDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let isArchive: boolean = false;
		for (let portalDocument of portalDocuments) {
			if (portalDocument.isArchive()) {
				isArchive = true;
				break;
			}
		}

		parameter!.mode = isArchive ? PdfaMode.ANALYZE : PdfaMode.CONVERT;

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			isArchive: isArchive
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: PortalPdfa = this.state.parameter!.data.clone();
		if (operationData.mode === PdfaMode.CONVERT) {
			operationData.analyze = undefined;

			let allowedLevel: Array<PdfaLevel> = [PdfaLevel._3a, PdfaLevel._3b, PdfaLevel._3u];
			if (!allowedLevel.includes(FunctionHelper.applyValue(
					operationData.convert?.level, ConvertPdfa.getLevelDefault
				)) || operationData.convert!.zugferd?.xmlFile?.value === "" ||
				!operationData.embedZugferd) {
				operationData.convert!.zugferd = undefined;
			}
		} else {
			operationData.convert = undefined;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		let operation: ConcurrentOperation = new ConcurrentOperation(this.props.concurrentOperations);
		for (let portalDocument of selection) {
			operation.add(async (resolve: () => void, reject: (exception: Error) => void) => {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: WebServiceTypes.PDFA,
						document: portalDocument,
						operation: Pdfa.fromJson(operationData.toJson()),
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as Settings)
					});

					resolve();
				} catch (exception: any) {
					if (this.props.concurrentOperations > 1) {
						let portalException: Exception = Exception.parseException(exception);
						portalDocument.setExecutionError(portalException);
					}

					reject(exception);
				}
			});
		}

		try {
			await operation.execute();
			let message: Message = new Message(
				operationData.mode === PdfaMode.CONVERT ? "success.pdfa.convert" : "success.pdfa.analyse",
				MessageType.SUCCESS
			);
			message.context = {count: operation.length};
			this.props.context.getPage().addMessage(message);
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
				return;
			}

			if (this.props.concurrentOperations > 1) {
				documentManager.updateGlobalDocumentList();
				this.hide();
			}
			return;
		}

		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.PDFA;
	}

	protected getDefaultParameter(): Parameter<PortalPdfa> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		let parameter: PortalPdfa | undefined = this.state.parameter?.data;
		let portalDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let isArchive: boolean = false;
		for (let portalDocument of portalDocuments) {
			if (portalDocument.isArchive()) {
				isArchive = true;
				break;
			}
		}

		parameter!.mode = isArchive ? PdfaMode.ANALYZE : PdfaMode.CONVERT;

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			isArchive: isArchive,
			zugferdFiles: new Map<ZugferdVersion, File>(),
			filePreview: ""
		});
	}
}

const PdfaDialog = React.memo(PdfaDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalPdfa>>,
		nextProps: Readonly<PortalDialogProperties<PortalPdfa>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PdfaDialog;